<template>
    <div class="pagination-control" :class="[{ stacked }]">
        <div v-if="pages.length > 1" class="actions" data-qa="actions">
            <DsIconButton
                name="arrow-left"
                class="prev-arrow"
                data-qa="prev"
                :disabled="offset === 0"
                @click="prev"
            />
            <div class="button-group">
                <DsDropdown v-if="hasPrefixPages">
                    <template #default>
                        <DsOutlineButton class="prefix-target" data-qa="prefix-dropdown">
                            <DsIcon name="more" />
                        </DsOutlineButton>
                    </template>

                    <template #menu>
                        <ol class="dropdown-menu-list">
                            <li
                                v-for="page in prefixPages"
                                :key="page.number"
                                class="dropdown-menu-item"
                                :data-qa="`page-${page.number}`"
                                @click="$emit('page', page)"
                            >
                                {{ $t('page', { pageNumber: page.number }) }}
                            </li>
                        </ol>
                    </template>
                </DsDropdown>

                <Component
                    :is="page.offset === offset ? 'ds-filled-button' : 'ds-outline-button'"
                    v-for="page in displayedPages"
                    :key="page.number"
                    :data-qa="`page-${page.number}`"
                    dense
                    @click="$emit('page', page)"
                >
                    {{ page.number }}
                </Component>

                <DsDropdown v-if="suffixPages.length" dense>
                    <template #default>
                        <DsOutlineButton class="suffix-target" data-qa="suffix-dropdown">
                            <DsIcon name="more" />
                        </DsOutlineButton>
                    </template>

                    <template #menu>
                        <ul class="dropdown-menu-list">
                            <li
                                v-for="page in suffixPages"
                                :key="page.number"
                                class="dropdown-menu-item"
                                :data-qa="`page-${page.number}`"
                                @click="$emit('page', page)"
                            >
                                {{ $t('page', { pageNumber: page.number }) }}
                            </li>
                        </ul>
                    </template>
                </DsDropdown>
            </div>
            <DsIconButton
                name="arrow-right"
                class="next-arrow"
                data-qa="next"
                :disabled="currentPageNumber === pageCount"
                @click="next"
            />
        </div>

        <p class="showing" data-qa="information">
            {{ $t('showing', {
                firstRecord: firstRecordNumber,
                lastRecord: lastRecordNumber,
                totalCount: totalCountDisplay,
            }) }}
        </p>
    </div>
</template>

<script>
const MAX_PAGE_BUTTONS = 6;

export default {
    name: 'AddContactPaginationControl',
    props: {
        totalCount: {
            type: Number,
            required: true,
        },
        limit: {
            type: Number,
            default: 50,
        },
        offset: {
            type: Number,
            required: true,
        },
        stacked: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['page'],

    computed: {
        totalCountDisplay() {
            return String(this.totalCount).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
        },

        firstRecordNumber() {
            return String(this.currentPage?.firstRecord || 0).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
        },

        lastRecordNumber() {
            return String(this.currentPage?.lastRecord || 0).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
        },

        pageCount() {
            return Math.ceil(this.totalCount / this.limit);
        },

        pages() {
            const pages = [];

            for (let page = 1; page <= this.pageCount; page++) {
                const offset = ((page - 1) * this.limit);
                const lastRecord = offset + this.limit > this.totalCount ? this.totalCount : offset + this.limit;

                pages.push({
                    number: page,
                    offset,
                    firstRecord: offset + 1,
                    lastRecord,
                });
            }

            return pages;
        },

        currentPage() {
            return this.pages.find(({ offset }) => this.offset === offset);
        },

        currentPageNumber() {
            return this.currentPage?.number || 0;
        },

        startingDisplayableOffset() {
            if (this.pageCount > this.numberedButtonCount && this.currentPageNumber > this.numberedButtonCount) {
                if (this.currentPageNumber > (this.pageCount - this.numberedButtonCount)) {
                    return this.pageCount - this.numberedButtonCount;
                }

                return Math.floor(this.currentPageNumber / this.numberedButtonCount) * this.numberedButtonCount - 1;
            }

            return 0;
        },

        endingDisplayableOffset() {
            return this.startingDisplayableOffset + this.numberedButtonCount;
        },

        hasPrefixPages() {
            return this.currentPageNumber >= MAX_PAGE_BUTTONS;
        },

        hasSuffixPages() {
            return this.currentPageNumber < this.pageCount - MAX_PAGE_BUTTONS;
        },

        numberedButtonCount() {
            return MAX_PAGE_BUTTONS - Number(this.hasPrefixPages) - Number(this.hasSuffixPages || this.currentPageNumber > 100) - Number(this.currentPageNumber > 1000);
        },

        displayedPages() {
            return this.pages.slice(this.startingDisplayableOffset, this.endingDisplayableOffset);
        },

        prefixPages() {
            return this.hasPrefixPages
                ? this.pages.slice(0, this.startingDisplayableOffset)
                : [];
        },

        suffixPages() {
            return this.endingDisplayableOffset >= this.pageCount
                ? []
                : this.pages.slice(this.endingDisplayableOffset);
        },
    },
    methods: {
        prev() {
            this.$emit('page', this.pages[this.currentPageNumber - 2]);
        },

        next() {
            this.$emit('page', this.pages[this.currentPageNumber]);
        },
    },
};
</script>

<style lang="scss" scoped>
    .pagination-control {
        display: grid;
        grid-gap: $spacing-100;
        grid-template-columns: auto auto;

        &.stacked {
            margin: 0;
            margin-bottom: $spacing-100;
            grid-template-columns: auto;
        }

        @media($small) {
            grid-template-columns: auto;
        }
    }

    .actions {
        --icon-size: #{$font-size-md};
        display: grid;
        justify-items: center;
        grid-template-columns: $spacing-400 auto $spacing-400;

        .button-group button {
            --icon-size: #{$font-size-sm};
            --icon-color: #{$color-ink};
            background-color: $color-gray-800;

            &.outline {
                color: $color-ink;
                background-color: $color-paper;
            }
        }
    }

    button.suffix-target,
    button.prefix-target {
        --button-padding-top: #{px-to-rem(8px)};
        --button-padding-right: 0.75rem;
        --button-padding-bottom: #{px-to-rem(5px)};
        --button-padding-left: 0.75rem;

        --icon-size: #{$font-size-md};
        margin-right: -1px;
        border-radius: 0;

        &.suffix-target {
            @include border-radius(0 $button-border-radius $button-border-radius 0);
        }

        &.prefix-target {
            @include border-radius($button-border-radius 0 0 $button-border-radius);
        }
    }


    .back-arrow {
        justify-self: start;
    }

    .forward-arrow {
        justify-self: end;
    }

    button[disabled] {
        --icon-color: #{$color-ink-600};
    }

    .showing {
        margin: $spacing-100 0;
        text-align: center;
    }
</style>
